import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, width: '100%' } as React.CSSProperties,
};

// ═══ 通话记录 ═══
function CallsView() {
  const [stats, setStats] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);

  useEffect(() => {
    get('/api/v1/calls-stats').then((r: any) => setStats(r.data)).catch(() => {});
    get('/api/v1/calls').then((r: any) => setRows(r.data || [])).catch(() => {});
  }, []);

  const open = async (id: string) => {
    try { const r: any = await get(`/api/v1/calls/${id}`); setDetail(r.data); } catch (e: any) { alert(e.message); }
  };

  return (
    <>
      {stats && (
        <div style={S.grid}>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('30天通话')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.total_calls}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('自助解决率')}</div><div style={{ fontSize: 24, fontWeight: 700, color: '#188038' }}>{stats.self_service_rate}%</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('WISMO 查单')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.wismo_calls}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('平均时长')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.avg_duration_s}s</div></div>
        </div>
      )}
      {detail && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong>📞 {detail.caller_number || t('通话')} · {detail.intent || '-'} · {detail.sentiment}</strong>
            <button style={S.btn} onClick={() => setDetail(null)}>{t('关闭')}</button>
          </div>
          {(detail.turns || []).map((t: any, i: number) => (
            <div key={i} style={{ marginBottom: 6, fontSize: 13, textAlign: t.role === 'agent' ? 'left' : 'right' }}>
              <span style={{
                display: 'inline-block', padding: '6px 12px', borderRadius: 12, maxWidth: '75%',
                background: t.role === 'agent' ? '#e8f0fe' : '#e6f4ea',
              }}>{t.text}</span>
            </div>
          ))}
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('来电')}</th><th style={S.th}>{t('语言')}</th><th style={S.th}>{t('意图')}</th><th style={S.th}>{t('情绪')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('订单')}</th><th style={S.th}>{t('时间')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
                <td style={S.td}>{r.caller_number || '-'}</td>
                <td style={S.td}>{r.language}</td>
                <td style={S.td}>{r.intent || '-'}</td>
                <td style={{ ...S.td, color: r.sentiment === 'negative' ? '#d93025' : r.sentiment === 'positive' ? '#188038' : '#666' }}>{r.sentiment}</td>
                <td style={S.td}>{r.status === 'transferred' ? '🔄 已转人工' : r.status}</td>
                <td style={S.td}>{r.matched_order_name || '-'}</td>
                <td style={S.td}>{(r.started_at || '').slice(0, 16)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={7}>{t('暂无通话记录 — 可在"对话模拟"测试')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 对话模拟器 ═══
function SimulatorView() {
  const [callId, setCallId] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<{ role: string; text: string }[]>([]);
  const [input, setInput] = useState('');
  const [lang, setLang] = useState('zh');
  const [busy, setBusy] = useState(false);

  const start = async () => {
    try {
      const r: any = await post('/api/v1/calls', { caller_number: 'simulator', language: lang });
      setCallId(r.data.call_id);
      setMsgs([{ role: 'agent', text: lang === 'zh' ? '您好！我是 AI 客服助手，请问有什么可以帮您？' : 'Hello! How can I help you today?' }]);
    } catch (e: any) { alert(e.message); }
  };

  const send = async () => {
    if (!callId || !input) return;
    const text = input;
    setInput('');
    setMsgs(m => [...m, { role: 'caller', text }]);
    setBusy(true);
    try {
      const r: any = await post(`/api/v1/calls/${callId}/turn`, { text });
      setMsgs(m => [...m, { role: 'agent', text: r.data.reply }]);
      if (r.data.action === 'transfer') {
        setMsgs(m => [...m, { role: 'system', text: `[已转接人工：${r.data.reason}]` }]);
        setCallId(null);
      }
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('对话模拟器（模拟 ASR 文本输入，验证编排逻辑）')}</h3>
      {!callId ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={{ ...S.input, width: 120 }} value={lang} onChange={e => setLang(e.target.value)}>
            <option value="zh">{t('中文')}</option><option value="en">English</option>
          </select>
          <button style={S.btnPrimary} onClick={start}>{t('📞 开始模拟通话')}</button>
        </div>
      ) : (
        <>
          <div style={{ minHeight: 200, maxHeight: 320, overflow: 'auto', marginBottom: 10, padding: 10, background: '#fafafa', borderRadius: 8 }}>
            {msgs.map((m, i) => (
              <div key={i} style={{ marginBottom: 6, textAlign: m.role === 'caller' ? 'right' : 'left', fontSize: 13 }}>
                <span style={{
                  display: 'inline-block', padding: '6px 12px', borderRadius: 12, maxWidth: '75%',
                  background: m.role === 'caller' ? '#e6f4ea' : m.role === 'system' ? '#fce8e6' : '#e8f0fe',
                }}>{m.text}</span>
              </div>
            ))}
            {busy && <div style={{ fontSize: 12, color: '#888' }}>{t('AI 思考中...')}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={S.input} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder={t('说点什么...（如：我的订单 #1001 到哪了）')} />
            <button style={S.btnPrimary} disabled={busy} onClick={send}>{t('发送')}</button>
            <button style={S.btn} onClick={async () => { await post(`/api/v1/calls/${callId}/end`, {}); setCallId(null); }}>{t('挂断')}</button>
          </div>
        </>
      )}
    </div>
  );
}

// ═══ 转接规则 ═══
function RulesView() {
  const [rules, setRules] = useState<any>(null);
  useEffect(() => { get('/api/v1/rules').then((r: any) => setRules(r.data)).catch(() => {}); }, []);
  if (!rules) return <div style={S.card}>{t('加载中...')}</div>;

  const save = async () => {
    try { await put('/api/v1/rules', rules); alert('已保存'); } catch (e: any) { alert(e.message); }
  };

  return (
    <div style={{ ...S.card, maxWidth: 520 }}>
      <h3 style={{ fontSize: 15, marginBottom: 16 }}>{t('人工转接规则')}</h3>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13 }}>
        <input type="checkbox" checked={!!rules.transfer_on_negative} onChange={e => setRules({ ...rules, transfer_on_negative: e.target.checked ? 1 : 0 })} />
        负面情绪 + 投诉类意图自动转人工
      </label>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{t('强制转人工的意图（逗号分隔）')}</label>
        <input style={S.input} value={rules.complex_intents} onChange={e => setRules({ ...rules, complex_intents: e.target.value })} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{t('人工客服电话')}</label>
        <input style={S.input} value={rules.human_phone} onChange={e => setRules({ ...rules, human_phone: e.target.value })} placeholder="+86..." />
      </div>
      <button style={S.btnPrimary} onClick={save}>{t('保存规则')}</button>
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('calls');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>VoiceAssistAI</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('多语言 AI 电话客服 — WISMO 查单 · 情绪识别 · 平滑转人工')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'calls')} onClick={() => setTab('calls')}>{t('通话记录')}</div>
        <div style={S.tab(tab === 'sim')} onClick={() => setTab('sim')}>{t('对话模拟')}</div>
        <div style={S.tab(tab === 'rules')} onClick={() => setTab('rules')}>{t('转接规则')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'calls' && <CallsView />}
      {tab === 'sim' && <SimulatorView />}
      {tab === 'rules' && <RulesView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
